/**
 * Verification Page for Re:Nav-96
 *
 * Public page for verifying event registrations and looking up tickets.
 * Supports both direct verification code entry and ticket lookup.
 */

import { useState, type FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router';
import type { EventRegistration, VerificationMethod } from '@/types';
import { registrationApi, ApiClientError } from '@/services/api';
import { TicketDisplay } from '@/components/public/TicketDisplay';
import '@/styles/animations.css';

/**
 * Verification mode - either verify a code or lookup a ticket
 */
type VerifyMode = 'verify' | 'lookup';

/**
 * VerifyPage Component
 *
 * Provides multiple flows for users to access their tickets:
 * 1. Enter verification code (for users who just registered)
 * 2. Look up existing ticket by email or ticket number
 *
 * @example
 * ```tsx
 * <VerifyPage />
 * ```
 */
export function VerifyPage() {
  // Get URL search params (ticket number may be passed from registration)
  const [searchParams] = useSearchParams();
  const initialTicketNumber = searchParams.get('ticket') || '';

  // Mode state
  const [mode, setMode] = useState<VerifyMode>(initialTicketNumber ? 'verify' : 'lookup');

  // Verification form state
  const [ticketNumber, setTicketNumber] = useState(initialTicketNumber);
  const [verificationCode, setVerificationCode] = useState('');
  const [verificationMethod, setVerificationMethod] = useState<VerificationMethod>('email');

  // Lookup form state
  const [lookupEmail, setLookupEmail] = useState('');
  const [lookupTicketNumber, setLookupTicketNumber] = useState('');

  // Result state
  const [registration, setRegistration] = useState<EventRegistration | null>(null);
  const [showTicket, setShowTicket] = useState(false);

  // UI state
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  /**
   * Handle verification code submission
   */
  const handleVerifySubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);
    setSuccessMessage(null);

    if (!ticketNumber.trim()) {
      setError('Please enter your ticket number');
      return;
    }

    if (!verificationCode.trim()) {
      setError('Please enter the verification code');
      return;
    }

    setIsLoading(true);

    try {
      const response = await registrationApi.verify({
        ticketNumber: ticketNumber.trim(),
        code: verificationCode.trim(),
        method: verificationMethod,
      });

      if (response.success && response.registration) {
        setRegistration(response.registration);
        setShowTicket(true);
        setSuccessMessage('Verification successful! Here is your ticket.');
      } else {
        setError(response.message || 'Verification failed. Please check your code and try again.');
      }
    } catch (err) {
      if (err instanceof ApiClientError) {
        setError(err.message);
      } else {
        setError('An unexpected error occurred. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Handle ticket lookup submission
   */
  const handleLookupSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);
    setSuccessMessage(null);

    if (!lookupEmail.trim() && !lookupTicketNumber.trim()) {
      setError('Please enter your email or ticket number');
      return;
    }

    setIsLoading(true);

    try {
      const response = await registrationApi.lookup({
        email: lookupEmail.trim() || undefined,
        ticketNumber: lookupTicketNumber.trim() || undefined,
      });

      if (response.found && response.registration) {
        setRegistration(response.registration);

        // Check if verification is complete
        if (response.registration.emailVerified || response.registration.phoneVerified) {
          setShowTicket(true);
          setSuccessMessage('Here is your ticket!');
        } else {
          // Switch to verification mode
          setTicketNumber(response.registration.ticketNumber);
          setMode('verify');
          setSuccessMessage('Registration found! Please enter your verification code to view your ticket.');
        }
      } else {
        setError(response.message || 'No registration found. Please check your details or register for the event.');
      }
    } catch (err) {
      if (err instanceof ApiClientError) {
        setError(err.message);
      } else {
        setError('An unexpected error occurred. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Handle resend verification code
   */
  const handleResendCode = async () => {
    if (!ticketNumber.trim()) {
      setError('Please enter your ticket number first');
      return;
    }

    setError(null);
    setSuccessMessage(null);
    setIsLoading(true);

    try {
      await registrationApi.resendVerification({
        ticketNumber: ticketNumber.trim(),
        method: verificationMethod,
      });
      setSuccessMessage(`Verification code resent via ${verificationMethod}!`);
    } catch (err) {
      if (err instanceof ApiClientError) {
        setError(err.message);
      } else {
        setError('Failed to resend verification code. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Reset to initial state
   */
  const handleReset = () => {
    setRegistration(null);
    setShowTicket(false);
    setTicketNumber('');
    setVerificationCode('');
    setLookupEmail('');
    setLookupTicketNumber('');
    setError(null);
    setSuccessMessage(null);
    setMode('lookup');
  };

  /**
   * Render the verification code form
   */
  const renderVerifyForm = () => (
    <form onSubmit={handleVerifySubmit} className="space-y-5">
      {/* Ticket Number Field */}
      <div>
        <label htmlFor="ticketNumber" className="block text-sm font-medium text-white/80 mb-2">
          Ticket Number
        </label>
        <input
          type="text"
          id="ticketNumber"
          name="ticketNumber"
          value={ticketNumber}
          onChange={(e) => setTicketNumber(e.target.value.toUpperCase())}
          className="input font-mono"
          placeholder="RN96-XXXX-XXXX"
          autoComplete="off"
          disabled={isLoading}
        />
        <p className="text-white/40 text-xs mt-1">
          Your ticket number was sent to you after registration
        </p>
      </div>

      {/* Verification Code Field */}
      <div>
        <label htmlFor="verificationCode" className="block text-sm font-medium text-white/80 mb-2">
          Verification Code
        </label>
        <input
          type="text"
          id="verificationCode"
          name="verificationCode"
          value={verificationCode}
          onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
          className="input text-center text-2xl tracking-widest"
          placeholder="000000"
          maxLength={6}
          autoComplete="one-time-code"
          disabled={isLoading}
        />
      </div>

      {/* Verification Method */}
      <div>
        <label className="block text-sm font-medium text-white/80 mb-3">
          Code was sent via
        </label>
        <div className="grid grid-cols-3 gap-3">
          <button
            type="button"
            onClick={() => setVerificationMethod('email')}
            disabled={isLoading}
            className={`p-3 rounded-lg border transition-all text-center ${
              verificationMethod === 'email'
                ? 'bg-primary/20 border-primary text-white'
                : 'bg-secondary-light border-white/10 text-white/60 hover:border-white/30'
            }`}
          >
            <svg className="w-5 h-5 mx-auto mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
            <span className="text-xs">Email</span>
          </button>
          <button
            type="button"
            onClick={() => setVerificationMethod('phone')}
            disabled={isLoading}
            className={`p-3 rounded-lg border transition-all text-center ${
              verificationMethod === 'phone'
                ? 'bg-primary/20 border-primary text-white'
                : 'bg-secondary-light border-white/10 text-white/60 hover:border-white/30'
            }`}
          >
            <svg className="w-5 h-5 mx-auto mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
            </svg>
            <span className="text-xs">SMS</span>
          </button>
          <button
            type="button"
            onClick={() => setVerificationMethod('whatsapp')}
            disabled={isLoading}
            className={`p-3 rounded-lg border transition-all text-center ${
              verificationMethod === 'whatsapp'
                ? 'bg-primary/20 border-primary text-white'
                : 'bg-secondary-light border-white/10 text-white/60 hover:border-white/30'
            }`}
          >
            <svg className="w-5 h-5 mx-auto mb-1" fill="currentColor" viewBox="0 0 24 24">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
            </svg>
            <span className="text-xs">WhatsApp</span>
          </button>
        </div>
      </div>

      {/* Submit Button */}
      <button
        type="submit"
        disabled={isLoading || verificationCode.length < 6}
        className="btn-primary w-full"
      >
        {isLoading ? (
          <span className="flex items-center justify-center gap-2">
            <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
            </svg>
            Verifying...
          </span>
        ) : (
          'Verify & View Ticket'
        )}
      </button>

      {/* Resend Code Link */}
      <div className="text-center">
        <button
          type="button"
          onClick={handleResendCode}
          disabled={isLoading}
          className="text-sm text-primary hover:text-primary-light transition-colors disabled:opacity-50"
        >
          Didn't receive the code? Resend
        </button>
      </div>
    </form>
  );

  /**
   * Render the ticket lookup form
   */
  const renderLookupForm = () => (
    <form onSubmit={handleLookupSubmit} className="space-y-5">
      <p className="text-white/60 text-sm text-center mb-4">
        Enter your email address or ticket number to look up your registration.
      </p>

      {/* Email Field */}
      <div>
        <label htmlFor="lookupEmail" className="block text-sm font-medium text-white/80 mb-2">
          Email Address
        </label>
        <input
          type="email"
          id="lookupEmail"
          name="lookupEmail"
          value={lookupEmail}
          onChange={(e) => setLookupEmail(e.target.value)}
          className="input"
          placeholder="your.email@example.com"
          autoComplete="email"
          disabled={isLoading}
        />
      </div>

      {/* OR Divider */}
      <div className="flex items-center gap-4">
        <div className="flex-1 border-t border-white/10" />
        <span className="text-white/40 text-sm">or</span>
        <div className="flex-1 border-t border-white/10" />
      </div>

      {/* Ticket Number Field */}
      <div>
        <label htmlFor="lookupTicketNumber" className="block text-sm font-medium text-white/80 mb-2">
          Ticket Number
        </label>
        <input
          type="text"
          id="lookupTicketNumber"
          name="lookupTicketNumber"
          value={lookupTicketNumber}
          onChange={(e) => setLookupTicketNumber(e.target.value.toUpperCase())}
          className="input font-mono"
          placeholder="RN96-XXXX-XXXX"
          autoComplete="off"
          disabled={isLoading}
        />
      </div>

      {/* Submit Button */}
      <button
        type="submit"
        disabled={isLoading || (!lookupEmail.trim() && !lookupTicketNumber.trim())}
        className="btn-primary w-full"
      >
        {isLoading ? (
          <span className="flex items-center justify-center gap-2">
            <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
            </svg>
            Looking up...
          </span>
        ) : (
          'Find My Ticket'
        )}
      </button>
    </form>
  );

  /**
   * Render the ticket display view
   */
  const renderTicketView = () => (
    <div className="space-y-6">
      {/* Success Icon */}
      <div className="text-center">
        <div className="w-16 h-16 mx-auto rounded-full bg-green-500/20 border-2 border-green-500 flex items-center justify-center mb-4">
          <svg className="w-8 h-8 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <h3 className="text-xl font-display font-bold text-white mb-2">
          Your Ticket
        </h3>
        <p className="text-white/60 text-sm">
          Save this ticket and present it at the venue on the event day.
        </p>
      </div>

      {/* Ticket Display */}
      {registration && (
        <TicketDisplay registration={registration} showDownload />
      )}

      {/* Actions */}
      <div className="space-y-3">
        <button
          onClick={handleReset}
          className="btn-secondary w-full"
        >
          Look Up Another Ticket
        </button>
        <Link to="/register" className="btn-secondary w-full block text-center">
          Register Another Person
        </Link>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen py-12 px-4">
      <div className="container mx-auto max-w-lg">
        {/* Header Section */}
        <div className="text-center mb-8 opacity-0 animate-fade-in-down" style={{ animationFillMode: 'forwards' }}>
          <div
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-4"
            style={{
              background: 'linear-gradient(90deg, rgba(233, 69, 96, 0.2) 0%, rgba(233, 69, 96, 0.05) 100%)',
              border: '1px solid rgba(233, 69, 96, 0.3)',
            }}
          >
            <svg className="w-4 h-4 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z" />
            </svg>
            <span className="text-sm font-medium text-white/80 uppercase tracking-wider">
              {showTicket ? 'Your Ticket' : 'Verify Registration'}
            </span>
          </div>
          <h1 className="text-3xl md:text-4xl font-display font-bold mb-4">
            <span className="text-gradient">
              {showTicket ? 'Here\'s Your Ticket' : 'Verify & View Ticket'}
            </span>
          </h1>
          {!showTicket && (
            <p className="text-white/60 max-w-md mx-auto">
              Enter your verification code to complete registration, or look up your existing ticket.
            </p>
          )}
        </div>

        {/* Main Card */}
        <div className="card opacity-0 animate-fade-in-up" style={{ animationDelay: '100ms', animationFillMode: 'forwards' }}>
          {/* Error Message */}
          {error && (
            <div className="mb-4 p-4 bg-red-500/10 border border-red-500/20 rounded-lg">
              <p className="text-red-400 text-sm">{error}</p>
            </div>
          )}

          {/* Success Message */}
          {successMessage && !showTicket && (
            <div className="mb-4 p-4 bg-green-500/10 border border-green-500/20 rounded-lg">
              <p className="text-green-400 text-sm">{successMessage}</p>
            </div>
          )}

          {/* Content based on state */}
          {showTicket ? (
            renderTicketView()
          ) : (
            <>
              {/* Mode Tabs */}
              <div className="flex mb-6 bg-secondary-light rounded-lg p-1">
                <button
                  onClick={() => {
                    setMode('verify');
                    setError(null);
                    setSuccessMessage(null);
                  }}
                  className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-all ${
                    mode === 'verify'
                      ? 'bg-primary text-white'
                      : 'text-white/60 hover:text-white'
                  }`}
                >
                  Enter Code
                </button>
                <button
                  onClick={() => {
                    setMode('lookup');
                    setError(null);
                    setSuccessMessage(null);
                  }}
                  className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-all ${
                    mode === 'lookup'
                      ? 'bg-primary text-white'
                      : 'text-white/60 hover:text-white'
                  }`}
                >
                  Find Ticket
                </button>
              </div>

              {/* Render current mode form */}
              {mode === 'verify' ? renderVerifyForm() : renderLookupForm()}
            </>
          )}
        </div>

        {/* Help Text */}
        {!showTicket && (
          <div className="text-center mt-8 opacity-0 animate-fade-in-up" style={{ animationDelay: '200ms', animationFillMode: 'forwards' }}>
            <p className="text-white/40 text-sm mb-2">
              Don't have a ticket yet?
            </p>
            <Link to="/register" className="link">
              Register for the Reunion
            </Link>
          </div>
        )}

        {/* Back to Home */}
        <div className="text-center mt-8">
          <Link to="/" className="link text-sm">
            <svg className="w-4 h-4 inline mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Back to Home
          </Link>
        </div>
      </div>
    </div>
  );
}

export default VerifyPage;
